/**
 * Type-narrowing check: a non-null value of `typeof === 'object'`. Returns
 * a record type so callers can index into it without further casts.
 */
export const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object';

/**
 * Walk an object along the given path of keys and return either the value at
 * the end of the walk OR `fallback` if any step is missing / non-object.
 *
 * Uses a nullish check (`?? fallback`) — NOT a truthy check — so legitimate
 * "missing" is only `null`/`undefined`. Numeric `0`, empty string `''` and
 * `false` are returned as-is. That distinction matters for callers like
 * `isNetworkError`, which probes `HttpErrorResponse.status` (0 = transport
 * failure — a real value, not a fallback trigger).
 *
 * @example
 *   const msg = safeGet(error, ['error', 'errors', 'storage', 0], 'Unknown error');
 */
export const safeGet = <T, R>(obj: T, path: Array<number | string>, fallback: R): R => {
  let cur: unknown = obj;

  for (const key of path) {
    if (cur == null || typeof cur !== 'object') return fallback;
    cur = (cur as Record<number | string, unknown>)[key];
  }

  return (cur ?? fallback) as R;
};

/**
 * Structural deep-equality check — the in-house replacement for lodash's
 * `isEqual`, so the app does not pull a whole utility library in for one
 * function.
 *
 * Handles what the app's cached / persisted state is actually made of:
 * primitives, arrays, plain objects, `Date` and `RegExp`, `Map` and `Set`,
 * nested to any depth. `NaN` equals `NaN` and `0`/`-0` are distinct
 * (`Object.is` semantics), matching lodash.
 *
 * Class instances are compared by their own enumerable properties, the same
 * way plain objects are — prototypes are not part of the comparison. Cyclic
 * structures are not supported and will overflow the stack; nothing in the
 * app stores cycles.
 *
 * Deliberately typed `unknown`/`unknown` rather than `<T>(a: T, b: T)`: callers
 * legitimately compare values whose declared types differ but whose runtime
 * shape is the same (e.g. a persisted `SortColumn[]` against the datagrid's
 * looser `Array<{ direction: string; field: string }>`), which a single type
 * parameter would reject at compile time.
 *
 * @example
 *   deepEqual({ key: 'eta', type: 'asc' }, { type: 'asc', key: 'eta' }); // true — key order is irrelevant
 *   deepEqual([{ field: 'eta' }], [{ field: 'etd' }]);                   // false
 */
export const deepEqual = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true;

  // Object.is already settled every case where either side is a primitive, so
  // a mismatch here means one side is an object and the other is not.
  if (!isObject(a) || !isObject(b)) return false;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;

    return a.every((item, index): boolean => deepEqual(item, b[index]));
  }

  if (a instanceof Date || b instanceof Date) {
    return a instanceof Date && b instanceof Date && Object.is(a.getTime(), b.getTime());
  }

  if (a instanceof RegExp || b instanceof RegExp) {
    return (
      a instanceof RegExp && b instanceof RegExp && a.source === b.source && a.flags === b.flags
    );
  }

  if (a instanceof Map || b instanceof Map) {
    if (!(a instanceof Map) || !(b instanceof Map) || a.size !== b.size) return false;

    // Keys are matched by identity (as `Map` itself does), values structurally.
    return [...a].every(([key, value]): boolean => b.has(key) && deepEqual(value, b.get(key)));
  }

  if (a instanceof Set || b instanceof Set) {
    if (!(a instanceof Set) || !(b instanceof Set) || a.size !== b.size) return false;

    return [...a].every((value): boolean => b.has(value));
  }

  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;

  return aKeys.every((key): boolean => Object.hasOwn(b, key) && deepEqual(a[key], b[key]));
};
